import * as React from "react";
import { ChevronDown, X } from "lucide-react";
import { Button } from "./button";
import { Badge } from "./badge";
import { Popover, PopoverContent, PopoverTrigger } from "ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "ui/command";
import { Checkbox } from "./checkbox";

export interface MultiSelectOption {
  label: string;
  value: string;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  className?: string;
  closeOnSelect?: boolean;
  closeOnOutsideClick?: boolean;
  disabled?: boolean;
  dropdownMaxHeight?: number;
}

export function MultiSelect({
  options,
  selected,
  onChange,
  placeholder = "Select items...",
  className = "",
  closeOnSelect = false,
  closeOnOutsideClick = false,
  disabled = false,
  dropdownMaxHeight = 240,
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open || !closeOnOutsideClick) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [open, closeOnOutsideClick]);

  const handleSelect = (value: string) => {
    let updated: string[];

    if (selected.includes(value)) {
      updated = selected.filter((item) => item !== value);
    } else {
      updated = [...selected, value];
    }

    onChange(updated);

    if (closeOnSelect) {
      setOpen(false);
    }
  };

  const handleRemove = (value: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(selected.filter((item) => item !== value));
  };

  return (
    <div ref={containerRef} className="contents">
    <Popover
      open={open}
      onOpenChange={(value) => {
        if (!disabled) {
          setOpen(value);
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={`h-auto min-h-6 w-full justify-between gap-1 overflow-visible border-0 bg-transparent px-0 py-0 shadow-none hover:bg-transparent focus-visible:ring-0 ${className}`}
          style={{ gap: "0px" }}
        >
          <div
            className="flex min-w-0 flex-1 flex-wrap items-center gap-1 pr-2"
          >
            {selected.length === 0 ? (
              <span className="text-muted-foreground text-sm">
                {placeholder}
              </span>
            ) : (
              <>
                {selected.map((value) => {
                  const option = options.find((opt) => opt.value === value);

                  return (
                    <Badge
                      key={value}
                      variant="secondary"
                      className="mr-1 text-xs shrink-0"
                      style={{
                        backgroundColor: "#5B9BD5",
                        color: "white",
                      }}
                    >
                      {option?.label}

                      <span
                        className="ml-1"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onClick={(e) => handleRemove(value, e)}
                      >
                        <X className="h-3 w-3" />
                      </span>
                    </Badge>
                  );
                })}
              </>
            )}
          </div>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        portalled={false}
        className="w-[var(--radix-popover-trigger-width)] p-0"
      >
        <Command>
          <CommandInput
            placeholder="Search..."
            className="h-9 focus:outline-none focus:ring-0 focus:border-transparent"
          />
          <CommandList
            className="visa-multi-select-list overscroll-contain"
            style={{
              maxHeight: `${dropdownMaxHeight}px`,
              overflowY: "scroll",
              scrollbarGutter: "stable",
            }}
          >
            <CommandEmpty>No item found.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  onSelect={() => handleSelect(option.value)}
                  className="cursor-pointer"
                >
                  <Checkbox
                    checked={selected.includes(option.value)}
                    className="mr-2 pointer-events-none"
                  />

                  <span>{option.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
    </div>
  );
}
